import { apiGet } from '../utils/apiUtils';
import { API_ENDPOINTS } from '../config/api';
import { getUserData } from './authService';

/**
 * Service to handle property-related API calls
 */

// Get all sell leads with pagination and date filtering
export const getSellLeads = async (page = 1, pageSize = 10, fromDate = null, toDate = null, uniqueId = null, city = null) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    let url = `${API_ENDPOINTS.PROPERTIES.SELL}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}`;
    
    // Add date parameters if provided
    if (fromDate) {
      url += `&fromDate=${fromDate}`;
    }
    if (toDate) {
      url += `&toDate=${toDate}`;
    }
    
    // Add filter parameters if provided
    if (uniqueId) {
      url += `&Unique_ID=${uniqueId}`;
    }
    if (city) {
      url += `&City=${city}`;
    }
    
    console.log('Sell API URL:', url); // Debug log
    
    const response = await apiGet(url);
    
    console.log('Sell API Raw Response:', response); // Debug log

    return response;
  } catch (error) {
    console.error('Error fetching sell leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch properties for sale.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

// Get sell property details
export const getSellPropertyDetails = async (propertyId) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    const url = `${API_ENDPOINTS.PROPERTIES.SELL}/details?Property_ID=${propertyId}&User_ID=${userData.User_ID}`;
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching property details:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch property details.',
      result: null
    };
  }
};

// Get all joint venture leads with pagination
export const getJointVentureLeads = async (page = 1, pageSize = 10, fromDate = null, toDate = null, uniqueId = null, city = null) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    let url = `${API_ENDPOINTS.PROPERTIES.JOINT_VENTURE}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}`;
    
    // Add date parameters if provided
    if (fromDate) {
      url += `&fromDate=${fromDate}`;
    }
    if (toDate) {
      url += `&toDate=${toDate}`;
    }
    
    // Add filter parameters if provided
    if (uniqueId) {
      url += `&Unique_ID=${uniqueId}`;
    }
    if (city) {
      url += `&City=${city}`;
    }
    
    const response = await apiGet(url);

    // Log response to debug
    if (response.success && response.result && response.result.data) {
      console.log('Joint Venture API Response data:', response.result.data);
    }

    return response;
  } catch (error) {
    console.error('Error fetching joint venture leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch joint venture properties.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

// Get joint venture property details
export const getJointVentureDetails = async (jvId) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    const url = `${API_ENDPOINTS.PROPERTIES.JOINT_VENTURE}/details?JV_ID=${jvId}&User_ID=${userData.User_ID}`;
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching joint venture details:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch joint venture details.',
      result: null
    };
  }
};

// Get all rent leads with pagination and date filtering
export const getRentLeads = async (page = 1, pageSize = 10, fromDate = null, toDate = null, uniqueId = null, city = null) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    let url = `${API_ENDPOINTS.PROPERTIES.RENT}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}`;
    
    // Add date parameters if provided
    if (fromDate) {
      url += `&fromDate=${fromDate}`;
    }
    if (toDate) {
      url += `&toDate=${toDate}`;
    }
    
    // Add filter parameters if provided
    if (uniqueId) {
      url += `&Unique_ID=${uniqueId}`;
    }
    if (city) {
      url += `&City=${city}`;
    }
    
    console.log('Rent API URL:', url); // Debug log
    
    const response = await apiGet(url);
    
    console.log('Rent API Raw Response:', response); // Debug log

    return response;
  } catch (error) {
    console.error('Error fetching rent leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch rental properties.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

// Get rent property details
export const getRentPropertyDetails = async (propertyId) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    const url = `${API_ENDPOINTS.PROPERTIES.RENT}/details?Property_ID=${propertyId}&User_ID=${userData.User_ID}`;
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching rental property details:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch rental property details.',
      result: null
    };
  }
};

// Get all agriculture leads with pagination
export const getAgricultureLeads = async (page = 1, pageSize = 10, fromDate = null, toDate = null, uniqueId = null, city = null) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    let url = `${API_ENDPOINTS.PROPERTIES.AGRICULTURE}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}&Property_Type=Sell`;
    
    // Add date parameters if provided
    if (fromDate) {
      url += `&fromDate=${fromDate}`;
    }
    if (toDate) {
      url += `&toDate=${toDate}`;
    }
    
    // Add filter parameters if provided
    if (uniqueId) {
      url += `&Unique_ID=${uniqueId}`;
    }
    if (city) {
      url += `&City=${city}`;
    }
    
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching agriculture leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch agriculture properties.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

export const getAgricultureYearlyContractLeads = async (page = 1, pageSize = 10, fromDate = null, toDate = null, uniqueId = null, city = null) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    let url = `${API_ENDPOINTS.PROPERTIES.AGRICULTURE}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}&Property_Type=Rent`;
    
    // Add date parameters if provided
    if (fromDate) {
      url += `&fromDate=${fromDate}`;
    }
    if (toDate) {
      url += `&toDate=${toDate}`;
    }
    
    // Add filter parameters if provided
    if (uniqueId) {
      url += `&Unique_ID=${uniqueId}`;
    }
    if (city) {
      url += `&City=${city}`;
    }
    
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching yearly contract agriculture leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch yearly contract agriculture properties.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

// Get all documents verification leads with pagination
export const getDocumentsVerificationLeads = async (page = 1, pageSize = 10) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    let url;
    if (userData.Role?.Name?.toLowerCase() === 'admin') {
      url = `${API_ENDPOINTS.PROPERTIES.DOCUMENTS_VERIFICATION_ADMIN}?page=${page}&pageSize=${pageSize}`;
    } else {
      url = `${API_ENDPOINTS.PROPERTIES.DOCUMENTS_VERIFICATION}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}`;
    }

    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching documents verification leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch documents verification data.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

// Get all investor leads with pagination
export const getInvestorLeads = async (page = 1, pageSize = 10) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    const url = `${API_ENDPOINTS.PROPERTIES.INVEST}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}`;
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching investor leads:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch investor data.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

// Download documents verification reports
export const downloadDocumentsReports = async () => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    const url = `${API_ENDPOINTS.PROPERTIES.DOCUMENTS_DOWNLOAD}?User_ID=${userData.User_ID}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${userData.token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download documents');
    }

    // Get the filename from the Content-Disposition header or use a default name
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/["']/g, '')
      : 'documents-report.pdf';

    // Convert the response to a blob
    const blob = await response.blob();

    // Create a download link and trigger the download
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true, message: 'Download started successfully' };
  } catch (error) {
    console.error('Error downloading documents:', error);
    return {
      success: false,
      message: error.message || 'Failed to download documents.',
    };
  }
};

// Get all property listings with pagination
export const getPropertyListings = async (page = 1, pageSize = 10) => {
  try {
    const userData = getUserData();
    if (!userData || !userData.User_ID) {
      throw new Error('User not authenticated');
    }

    const url = `${API_ENDPOINTS.PROPERTIES.LISTINGS}?User_ID=${userData.User_ID}&page=${page}&pageSize=${pageSize}`;
    const response = await apiGet(url);

    return response;
  } catch (error) {
    console.error('Error fetching property listings:', error);
    return {
      error_code: error.response?.data?.error_code || 500,
      success: false,
      message: error.response?.data?.message || 'Failed to fetch property listings.',
      result: {
        totalRecords: 0,
        totalPages: 0,
        page: page,
        pageSize: pageSize,
        data: []
      }
    };
  }
};

//For Dashboard Mainpage Stats
